import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

// Cache global pour éviter des requêtes redondantes (uniquement pour les comptages par set)
const countCache: Record<string, { counts: Record<string, number>; timestamp: number }> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook optimisé qui récupère en une seule requête le nombre de cartes par set
 * ainsi que le nombre de cartes collectées par l'utilisateur
 */
export function useCachedSetsCardCounts() {
  const [cardCounts, setCardCounts] = useState<Record<string, number>>({});
  const [collectedCounts, setCollectedCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchCardCounts = async () => {
      // Vérifier le cache global
      const cacheKey = 'set_card_counts';
      if (countCache[cacheKey] && Date.now() - countCache[cacheKey].timestamp < CACHE_TTL) {
        setCardCounts(countCache[cacheKey].counts);
        // Ne pas mettre fin au chargement ici, car on doit encore récupérer les cartes collectées
      } else {
        try {
          // Récupérer le nombre de cartes par set en un seul appel à la base de données
          const { data, error } = await supabase
            .rpc('get_card_counts_by_set');

          if (error) throw error;

          const counts: Record<string, number> = {};
          if (data) {
            // Convertir les données reçues en un objet indexé par code de set
            data.forEach((item: { set: string; count: number }) => {
              counts[item.set] = item.count;
            });
          }

          // Mettre à jour le cache global
          countCache[cacheKey] = {
            counts,
            timestamp: Date.now()
          };

          setCardCounts(counts);
        } catch (err) {
          console.error('Erreur lors de la récupération du nombre de cartes par set:', err);
        }
      }
    };

    const fetchCollectedCounts = async () => {
      if (!user) {
        setCollectedCounts({});
        setLoading(false);
        return;
      }

      try {
        // Récupérer le nombre de cartes collectées par set en un seul appel
        // IMPORTANT: Ne pas mettre en cache ces données car elles changent fréquemment
        const { data, error } = await supabase
          .rpc('get_user_card_counts_by_set', { user_id_param: user.id });

        if (error) throw error;

        const counts: Record<string, number> = {};
        if (data) {
          data.forEach((item: { set: string; count: number }) => {
            counts[item.set] = item.count;
          });
        }

        setCollectedCounts(counts);
      } catch (err) {
        console.error('Erreur lors de la récupération des cartes collectées:', err);
      } finally {
        setLoading(false);
      }
    };

    // Exécuter les deux requêtes en parallèle
    fetchCardCounts();
    fetchCollectedCounts();

  }, [user?.id]);

  return {
    cardCounts,
    collectedCounts,
    loading,
    // Fonction pour obtenir les statistiques d'un set spécifique
    getSetStats: (setCode: string) => ({
      totalCards: cardCounts[setCode] || 0,
      collectedCards: collectedCounts[setCode] || 0,
      loading
    }),
    // Fonction pour rafraîchir les comptages de collection
    refreshCollectionCounts: async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .rpc('get_user_card_counts_by_set', { user_id_param: user.id });

        if (error) throw error;

        const counts: Record<string, number> = {};
        if (data) {
          data.forEach((item: { set: string; count: number }) => {
            counts[item.set] = item.count;
          });
        }

        setCollectedCounts(counts);
      } catch (err) {
        console.error('Erreur lors du rafraîchissement des cartes collectées:', err);
      } finally {
        setLoading(false);
      }
    }
  };
} 